import React from "react";
import { Avatar, Box, Typography } from "@mui/material";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import {
  floorPersonOpenTitle,
  sanitizeAvatarUrl,
  videoInitials,
  voFloorMediaFlags,
  voFloorMediaFlagsPlacement,
  voFloorSeatBoxSize,
} from "./videoMediaPresence";
import { floorPresenceCopy } from "./voFloorLiving";
import { voCommStatusMark } from "../VirtualOffice/voCommunicationStatus";

const MEDIA_FLAG_ICONS = {
  muted: MicOffIcon,
  camOff: VideocamOffIcon,
  speakerMuted: VolumeOffOutlinedIcon,
};

function SeatMediaFlags({
  muted = false,
  camOff = false,
  speakerMuted = false,
  compact = false,
  crowd = "roomy",
  live = false,
}) {
  const flags = voFloorMediaFlags({ muted, camOff, speakerMuted });
  if (!flags.length) return null;
  const place = voFloorMediaFlagsPlacement({ crowd, compact, camOn: live });
  return (
    <Box
      data-vo-media-flags="1"
      data-vo-media-hang={String(place.hangPx)}
      sx={{
        position: "absolute",
        right: `-${place.hangPx}px`,
        bottom: `-${place.hangPx}px`,
        display: "flex",
        flexDirection: "row",
        gap: "2px",
        zIndex: 3,
        pointerEvents: "none",
      }}
    >
      {flags.map((flag) => {
        const Icon = MEDIA_FLAG_ICONS[flag.key];
        if (!Icon) return null;
        return (
          <Box
            key={flag.key}
            data-vo-flag={flag.key}
            title={flag.title}
            aria-label={flag.title}
            sx={{
              width: place.chipPx,
              height: place.chipPx,
              borderRadius: "50%",
              bgcolor: "rgba(36, 22, 18, 0.88)",
              border: "1px solid rgba(242,139,130,0.45)",
              color: "#f28b82",
              display: "grid",
              placeItems: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.32)",
            }}
          >
            <Icon sx={{ fontSize: place.iconPx }} />
          </Box>
        );
      })}
    </Box>
  );
}

function SeatMark({ kind, emoji, text, title }) {
  const birthday = kind === "birthday";
  const status = kind === "status";
  return (
    <Box
      data-vo-birthday={birthday ? "1" : undefined}
      data-vo-status={status ? "1" : undefined}
      data-vo-kudos={!birthday && !status ? "1" : undefined}
      title={title}
      aria-label={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.45,
        px: 0.75,
        py: 0.25,
        borderRadius: 8,
        bgcolor: birthday ? "rgba(62, 28, 40, 0.94)" : "rgba(42, 32, 18, 0.94)",
        border: birthday ? "1px solid rgba(243,196,216,0.7)" : "1px solid rgba(232,196,138,0.7)",
        color: birthday ? "#fde8f0" : "#f8edd9",
        fontSize: "0.62rem",
        fontWeight: 800,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        boxShadow: "0 4px 12px rgba(0,0,0,0.38)",
        pointerEvents: "none",
      }}
    >
      <Box component="span" aria-hidden>
        {emoji}
      </Box>
      {text ? (
        <Box component="span" data-vo-mark-text="1">
          {text}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Assento do piso: foto + nome + status. Vídeo, se houver, cobre o mesmo lugar.
 */
export default function VoFloorSeat({
  name = "",
  avatarUrl = "",
  self = false,
  status = "present",
  speaking = false,
  muted = false,
  camOff = false,
  speakerMuted = false,
  compact = false,
  crowd = "roomy",
  live = false,
  primary = "#1a73e8",
  cargo = "",
  bare = false,
  birthday = false,
  kudosEmoji = "",
  kudosLabel = "",
  kudosTitle = "",
  joinedAt = null,
  now = Date.now(),
  timeZone = "",
  onOpen = null,
  children = null,
}) {
  const dense = crowd === "busy" || crowd === "packed";
  const packed = crowd === "packed";
  const copy = floorPresenceCopy({
    self,
    name,
    status,
    compact: compact || dense,
    cargo,
    joinedAt,
    now,
    timeZone,
  });
  const statusMark = voCommStatusMark(status);
  const src = sanitizeAvatarUrl(avatarUrl);
  const initials = videoInitials(name || (self ? "Você" : "Colega"));
  const size = compact ? 52 : live ? "76%" : "62%";
  const sizeMax = compact ? 52 : voFloorSeatBoxSize({ camOn: live, crowd }).cam;
  const canOpen = Boolean(typeof onOpen === "function");
  const openTitle = floorPersonOpenTitle(copy.title, { self, live });
  const seal = String(kudosEmoji || "").trim();

  return (
    <Box
      data-vo-floor-seat="1"
      data-vo-floor-click={canOpen ? "1" : undefined}
      data-speaking={speaking ? "1" : "0"}
      data-muted={muted ? "1" : "0"}
      data-cam-off={camOff ? "1" : "0"}
      data-speaker-muted={speakerMuted ? "1" : "0"}
      data-vo-live-cam={live ? "1" : "0"}
      role={canOpen ? "button" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      title={canOpen ? openTitle : undefined}
      aria-label={canOpen ? openTitle : undefined}
      onClick={
        canOpen
          ? (e) => {
              e.stopPropagation();
              onOpen(e);
            }
          : undefined
      }
      onKeyDown={
        canOpen
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen(e);
              }
            }
          : undefined
      }
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: compact || dense ? 0.35 : { xs: 0.45, md: live ? 1.1 : 1 },
        px: 1,
        pb: 0.5,
        cursor: canOpen ? (self && live ? "zoom-in" : "pointer") : "default",
        background: bare
          ? "transparent"
          : "linear-gradient(180deg, rgba(32,28,24,0.08) 0%, rgba(20,16,14,0.28) 100%)",
        "&:hover [data-vo-seat-avatar]": canOpen
          ? {
              boxShadow: speaking
                ? "0 0 0 3px rgba(232,196,138,0.95), 0 0 0 6px rgba(138,180,248,0.55), 0 10px 28px rgba(0,0,0,0.35)"
                : "0 0 0 3px rgba(232,196,138,0.92), 0 12px 28px rgba(0,0,0,0.4)",
            }
          : undefined,
        "& [data-vo-click-hint]": {
          opacity: 0,
          transform: "translateX(-50%) translateY(6px)",
        },
        "&:hover [data-vo-click-hint]": canOpen
          ? { opacity: 1, transform: "translateX(-50%) translateY(0)" }
          : undefined,
      }}
    >
      <Box
        sx={{
          position: "relative",
          flexShrink: 0,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          overflow: "visible",
        }}
      >
        <Box
          data-vo-seat-avatar="1"
          sx={{
            position: "relative",
            width: size,
            maxWidth: sizeMax,
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            overflow: "visible",
            zIndex: 1,
            boxShadow: speaking
              ? "0 0 0 3px rgba(138,180,248,0.95), 0 10px 28px rgba(0,0,0,0.35)"
              : "0 10px 18px rgba(16,10,8,0.4), 0 2px 0 rgba(255,220,180,0.12)",
            transition: "box-shadow 140ms ease",
          }}
        >
          <Avatar
            src={src || undefined}
            alt={name || ""}
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: primary,
              fontSize: compact || packed ? "0.85rem" : dense ? "1rem" : { xs: "1.1rem", md: "1.7rem" },
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          {children ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                overflow: "hidden",
              }}
            >
              {children}
            </Box>
          ) : null}
          <SeatMediaFlags
            muted={muted}
            camOff={camOff}
            speakerMuted={speakerMuted}
            compact={compact}
            crowd={crowd}
            live={live}
          />
        </Box>
        {birthday || seal || statusMark ? (
          <Box
            data-vo-floor-marks="1"
            sx={{
              position: "absolute",
              top: compact ? -6 : { xs: -4, md: -8 },
              right: compact ? -10 : { xs: -4, md: -16 },
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            {statusMark ? (
              <SeatMark
                kind="status"
                emoji={statusMark.emoji}
                text={packed ? "" : statusMark.text}
                title={statusMark.title}
              />
            ) : null}
            {birthday ? (
              <SeatMark
                kind="birthday"
                emoji="🎂"
                text={packed ? "" : "Aniversário"}
                title="Aniversário hoje"
              />
            ) : null}
            {seal ? (
              <SeatMark
                kind="kudos"
                emoji={seal}
                text={packed ? "" : kudosLabel || "Selo"}
                title={kudosTitle || "Selo do dia"}
              />
            ) : null}
          </Box>
        ) : null}
        {canOpen && !self ? (
          <Box
            data-vo-click-hint="1"
            sx={{
              position: "absolute",
              left: "50%",
              bottom: -6,
              zIndex: 8,
              pointerEvents: "none",
              px: 0.9,
              py: 0.15,
              borderRadius: 8,
              bgcolor: "rgba(28, 22, 14, 0.92)",
              border: "1px solid rgba(232,196,138,0.45)",
              color: "#f8edd9",
              fontSize: "0.62rem",
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
              transition: "opacity 140ms ease, transform 140ms ease",
            }}
          >
            Falar
          </Box>
        ) : null}
      </Box>
      <Typography
        sx={{
          color: "#f8f4ee",
          fontSize: compact || packed ? "0.68rem" : dense ? "0.74rem" : { xs: "0.78rem", md: "0.95rem" },
          fontWeight: 700,
          textAlign: "center",
          lineHeight: 1.2,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textShadow: "0 1px 2px rgba(16,10,8,0.75)",
        }}
      >
        {copy.title}
      </Typography>
      {packed ? null : (
        <Typography
          sx={{
            color: speaking ? "#c4d7fb" : "#d7cfc4",
            fontSize: compact || dense ? "0.58rem" : { xs: "0.62rem", md: "0.72rem" },
            fontWeight: 600,
            textAlign: "center",
            textShadow: "0 1px 2px rgba(16,10,8,0.7)",
          }}
        >
          {copy.body}
        </Typography>
      )}
      {packed || compact || !copy.connected ? null : (
        <Typography
          data-vo-seat-connected="1"
          sx={{
            color: "#c9c0b4",
            fontSize: dense ? "0.52rem" : { xs: "0.56rem", md: "0.64rem" },
            fontWeight: 600,
            textAlign: "center",
            textShadow: "0 1px 2px rgba(16,10,8,0.7)",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {copy.connected}
        </Typography>
      )}
    </Box>
  );
}
